import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import moment from "moment";

const downloadChecklistPDF = (data: any) => {
  let parsed: any = null;

  try {
    parsed = data.Current_json ? JSON.parse(data.Current_json) : null;
  } catch (e) {
    console.error("Invalid JSON in Current_json", e);
    alert("Invalid data format. Cannot generate PDF.");
    return;
  }

  if (!parsed) {
    alert("No checklist data available.");
    return;
  }

  // ==================== HELPER FUNCTIONS FOR FILTERING ====================
  
  // Meaningful value checks (same as in PreviewChecklist)
  const isMeaningfulValue = (value: any): boolean => {
    if (value === null || value === undefined) return false;
    if (typeof value === "boolean") return value === true;
    if (typeof value === "string") return value.trim() !== "";
    if (typeof value === "number") return true;
    if (Array.isArray(value)) return value.some(isMeaningfulValue);
    if (typeof value === "object") {
      // Special handling for objects with "value" property
      if ("value" in value) {
        return isMeaningfulValue(value.value) || value.verified === true;
      }
      return Object.values(value).some(isMeaningfulValue);
    }
    return false;
  };

  const isMeaningfulRow = (row: any): boolean => {
    if (!row || typeof row !== "object") return false;
    
    // Check for any meaningful value in the row
    for (const key in row) {
      if (isMeaningfulValue(row[key])) {
        return true;
      }
    }
    return false;
  };

  const hasMeaningfulRows = (rows: any[]): boolean => {
    if (!Array.isArray(rows)) return false;
    return rows.some(isMeaningfulRow);
  };

  const cleanRows = (rows: any[]) => rows?.filter(isMeaningfulRow) || [];

  // Helper function to check if a specific subsection should be shown
  const shouldShowSubSection = (section: any, showStatusKey: string): boolean => {
    if (section[showStatusKey] === undefined) return true; // No show status, default to show if has data
    return section[showStatusKey] === true || section[showStatusKey] === "true";
  };

  // Check if entire section has meaningful data (same as in PreviewChecklist)
  const isSectionMeaningful = (key: string): boolean => {
    if (!parsed || !parsed[key]) return false;

    const section = parsed[key];

    // Function to check if section has meaningful data
    const hasMeaningfulData = (): boolean => {
      switch (key) {
        case "generalVerification":
          // Check if section has a show status
          if (!shouldShowSubSection(section, "isshowgeneralVerificationStatus")) return false;
          
          return (
            isMeaningfulValue(section.isSpellingCorrect) ||
            isMeaningfulValue(section.ownerShip) ||
            (section.file && Object.values(section.file).some(isMeaningfulValue))
          );

        case "surveyNoVerification":
          // Check each subsection independently
          const shouldShowRegister = shouldShowSubSection(section, "isshowregisterStatus");
          const shouldShowCurrentPatta = shouldShowSubSection(section, "isshowcurrentPattaStatus");
          const shouldShowTitleDeed = shouldShowSubSection(section, "isshowtitleDeedStatus");
          
          // Only check data for subsections that should be shown
          const hasRegisterData = shouldShowRegister ? hasMeaningfulRows(section.register || []) : false;
          const hasCurrentPattaData = shouldShowCurrentPatta ? hasMeaningfulRows(section.currentPatta || []) : false;
          const hasTitleDeedData = shouldShowTitleDeed ? hasMeaningfulRows(section.titleDeed || []) : false;
          
          // Check other fields (these should always show if they have data, as they don't have individual show status)
          const hasOtherData = 
            isMeaningfulValue(section.totalTitleDeed) ||
            isMeaningfulValue(section.totalEc) ||
            isMeaningfulValue(section.difference) ||
            isMeaningfulValue(section.missingEc) ||
            section.type === true ||
            section.conditionPatta === true;
          
          return hasRegisterData || hasCurrentPattaData || hasTitleDeedData || hasOtherData;

        case "deed":
          // Check each deed type's show status
          const deedTypes = [
            { key: "saleDeed", showStatus: "isshowsaleDeedStatus" },
            { key: "giftDeed", showStatus: "isshowgiftDeedStatus" },
            { key: "settlementDeed", showStatus: "isshowsettlementDeedStatus" },
            { key: "partitionDeed", showStatus: "isshowpartitionDeedStatus" },
            { key: "willDeed", showStatus: "isshowwillDeedStatus" },
            { key: "leaseDeed", showStatus: "isshowleaseDeedStatus" },
            { key: "attorneyDeed", showStatus: "isshowattorneyDeedStatus" },
            { key: "releaseDeed", showStatus: "isshowreleaseDeedStatus" },
            { key: "rectificationDeed", showStatus: "isshowrectificationDeedStatus" },
            { key: "consentDeed", showStatus: "isshowconsentDeedStatus" },
            { key: "missingDocument", showStatus: "isshowmissingDocumentStatus" },
          ];
          
          let hasAnyDeedData = false;
          
          // Check each deed type if its show status is true
          for (const deedType of deedTypes) {
            if (shouldShowSubSection(section, deedType.showStatus)) {
              if (hasMeaningfulRows(section[deedType.key] || [])) {
                hasAnyDeedData = true;
                break;
              }
            }
          }
          
          // Check other deed-related fields
          const hasPlotRegularization = shouldShowSubSection(section, "ishowplotregulationstatus") &&
            section.plotRegularization &&
            (section.plotRegularization.isDeedRegisteredAfter30_9_2016 === true ||
              section.plotRegularization.isPlotObtained === true);

          const hasForeignPower = shouldShowSubSection(section, "ishowAdjudicationstatus") &&
            section.foriegnPower &&
            (section.foriegnPower.isForiegnPowerRegistered === true ||
              isMeaningfulValue(section.foriegnPower.docNo));

          const hasMortgageInPowerDoc = section.mortgageInPowerDoc === true;

          return (
            hasAnyDeedData ||
            hasPlotRegularization ||
            hasForeignPower ||
            hasMortgageInPowerDoc
          );

        case "HsdpattasVerify":
          // Only show if show status is true
          if (!shouldShowSubSection(section, "isshowHsdpattasStatus")) return false;
          return hasMeaningfulRows(section.Hsdpattas || []);

        case "nathamPattaverify":
          if (!shouldShowSubSection(section, "isshownathamPattaStatus")) return false;
          return hasMeaningfulRows(section.nathamPatta || []);

        case "legalHeirsVerify":
          if (!shouldShowSubSection(section, "isshowlegalHeirsStatus")) return false;
          return hasMeaningfulRows(section.legalHeirs || []);

        case "ecVerify":
          // ecVerify doesn't have show status in your data, so always check data
          return hasMeaningfulRows(section.ecentry || []);

        case "flow":
          if (!shouldShowSubSection(section, "isshowflowStatus")) return false;
          return hasMeaningfulRows(section.flow || []);

        case "propertytax":
          if (!shouldShowSubSection(section, "isshowpropertytaxStatus")) return false;
          return hasMeaningfulRows(section.propertytax || []);

        case "EBreceiptData":
          if (!shouldShowSubSection(section, "isshowEBreceiptStatus")) return false;
          return hasMeaningfulRows(section.EBreceipt || []);

        case "missingLOD":
          // missingLOD doesn't have show status in your data, so always check data
          return hasMeaningfulRows(section.sheets || []);

        case "verification":
          // Check each verification type
          const verificationTypes = [
            { key: "company", showStatus: "isshowcompanyStatus" },
            { key: "trust", showStatus: "isshowtrustStatus" },
            { key: "partnership", showStatus: "isshowpartnershipStatus" },
            { key: "association", showStatus: "isshowassociationStatus" },
            { key: "society", showStatus: "isshowsocietyStatus" },
            { key: "appartment", showStatus: "isshowappartmentStatus" },
          ];
          
          for (const type of verificationTypes) {
            if (shouldShowSubSection(section, type.showStatus)) {
              const subSection = section[type.key];
              if (subSection && Object.values(subSection).some(isMeaningfulValue)) {
                return true;
              }
            }
          }
          return false;

        case "courtDocVerification":
          // Check each court doc type
          const courtDocTypes = [
            { key: "partitionSuit", showStatus: "isshowPartitionSuitStatus" },
            { key: "declarationSuit", showStatus: "isshowDeclarationSuitStatus" },
            { key: "lokAdalat", showStatus: "isshowLokAdalatStatus" },
            { key: "specificSuit", showStatus: "isshowSpecificSuitStatus" },
            { key: "appeal", showStatus: "isshowAppealStatus" },
            { key: "courtAuctionPurchase", showStatus: "isshowCourtAuctionPurchaseStatus" },
            { key: "sarfesiProceeding", showStatus: "isshowSarfesiProceedingStatus" },
          ];
          
          // Also check the main court doc status
          if (shouldShowSubSection(section, "isshowCourtDocStatus")) {
            for (const type of courtDocTypes) {
              const subSection = section[type.key];
              if (subSection && Object.values(subSection).some(isMeaningfulValue)) {
                return true;
              }
            }
          }
          
          // Check individual court doc types
          for (const type of courtDocTypes) {
            if (shouldShowSubSection(section, type.showStatus)) {
              const subSection = section[type.key];
              if (subSection && Object.values(subSection).some(isMeaningfulValue)) {
                return true;
              }
            }
          }
          return false;

        case "originalDocVerification":
        case "remarks":
          // These don't have show status fields, so always check for data
          return Object.values(section).some(isMeaningfulValue);

        default:
          return Object.values(section).some(isMeaningfulValue);
      }
    };

    return hasMeaningfulData();
  };

  // Get only meaningful sections (same logic as PreviewChecklist)
  const meaningfulSections = parsed
    ? Object.keys(parsed).filter(isSectionMeaningful)
    : [];

  // If no meaningful sections, show a message
  if (meaningfulSections.length === 0) {
    alert("No meaningful checklist data to generate PDF.");
    return;
  }

  const doc = new jsPDF("p", "mm", "a4");
  let yPos = 20;

  // ==================== HEADER ====================
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text(`${data.title} - Checklist Preview`, 105, yPos, { align: "center" });

  yPos += 12;
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.text(
    `${data.file_id} • Login at ${moment(data.login_dt).format("DD MMMM YYYY, hh:mm A")} • ${data.bank_name}`,
    105,
    yPos,
    { align: "center" },
  );

  yPos += 10;
  doc.text(
    `Process Checklist Completed at ${moment(data.date).format("DD MMMM YYYY, hh:mm A")} • ${data.officerType} • Updated by ${data.downloadedBy}`,
    105,
    yPos,
    { align: "center" },
  );

  yPos += 10;

  // ==================== UTILITIES ====================
  const checkPageBreak = (needed = 0) => {
    const pageHeight = doc.internal.pageSize.height;
    const bottomMargin = 22; // footer + breathing space

    if (yPos + needed > pageHeight - bottomMargin) {
      doc.addPage();
      yPos = 20;
      return true;
    }
    return false;
  };

  const addSectionTitle = (title: string, bgColor?: string) => {
    const leftMargin = 10;
    const boxWidth = 190;
    const boxHeight = 10;
    const topPadding = 0;
    const bottomPadding = 8;

    checkPageBreak(topPadding + boxHeight + bottomPadding);
    yPos += topPadding;

    const fillColor: [number, number, number] =
      bgColor === "lightgreen"
        ? [212, 237, 218]
        : bgColor === "#ffff8d"
          ? [255, 243, 205]
          : [0, 123, 255];

    const textColor: [number, number, number] =
      bgColor === "lightgreen"
        ? [21, 87, 36]
        : bgColor === "#ffff8d"
          ? [133, 100, 4]
          : [255, 255, 255];

    doc.setFillColor(...fillColor);
    doc.rect(leftMargin, yPos, boxWidth, boxHeight, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(...textColor);
    doc.text(title, leftMargin + 5, yPos + boxHeight / 2 + 3);

    const textWidth = doc.getTextWidth(title);
    doc.setDrawColor(...textColor);
    doc.setLineWidth(0.5);
    doc.line(
      leftMargin + 5,
      yPos + boxHeight / 2 + 4,
      leftMargin + 5 + textWidth,
      yPos + boxHeight / 2 + 4,
    );

    yPos += boxHeight + bottomPadding;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
  };

  const addSubSection = (title: string) => {
    checkPageBreak(12); // title + spacing

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(title, 15, yPos);

    yPos += 8;
  };

const addSmartFields = (pairs: { label: string; value: any }[]) => {
  if (pairs.length === 0) return;

  const lineHeight = 6;
  
  // Layout positions - FIXED X coordinates for alignment
  const leftColumnLabelX = 15;
  const leftColumnValueX = 80;  // Fixed position for all left values
  const rightColumnLabelX = 113;
  const rightColumnValueX = 180; // Fixed position for all right values
  
  const leftColumnLabelWidth = 60;
  const rightColumnLabelWidth = 60;
  const maxValueWidthTwoColumn = 25;
  
  const singleColumnLabelX = 15;
  const singleColumnValueX = 110; // Fixed position for all single-column values
  const singleColumnLabelWidth = 90;
  const singleColumnValueWidth = 85;

  const renderRow = (
    label: string,
    value: any,
    xLabel: number,
    xValue: number,
    labelWidth: number,
    valueWidth: number,
  ) => {
    const val = formatValue(value);
    
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    const labelLines = doc.splitTextToSize(`${label}:`, labelWidth);
    
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    const valueLines = doc.splitTextToSize(val, valueWidth);

    const labelHeight = labelLines.length * lineHeight;
    const valueHeight = valueLines.length * lineHeight;
    const rowHeight = Math.max(labelHeight, valueHeight);

    checkPageBreak(rowHeight + 2);

    const startY = yPos;

    // Draw label lines
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    let currentY = startY;
    for (let i = 0; i < labelLines.length; i++) {
      doc.text(labelLines[i], xLabel, currentY);
      currentY += lineHeight;
    }

    // Draw value lines
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    currentY = startY;
    for (let i = 0; i < valueLines.length; i++) {
      doc.text(valueLines[i], xValue, currentY);
      currentY += lineHeight;
    }

    return rowHeight;
  };

  // Determine which items go in two-column vs single-column
  const shortPairs = pairs.filter(p => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    return doc.getTextWidth(`${p.label}:`) < 55;
  });
  
  const longPairs = pairs.filter(p => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    return doc.getTextWidth(`${p.label}:`) >= 55;
  });

  // Two-column layout
  for (let i = 0; i < shortPairs.length; i += 2) {
    const left = shortPairs[i];
    const right = shortPairs[i + 1];

    let leftHeight = lineHeight;
    let rightHeight = lineHeight;

    if (left) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      const leftLabelLines = doc.splitTextToSize(`${left.label}:`, leftColumnLabelWidth);
      const leftVal = formatValue(left.value);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      const leftValLines = doc.splitTextToSize(leftVal, maxValueWidthTwoColumn);
      leftHeight = Math.max(leftLabelLines.length, leftValLines.length) * lineHeight;
    }

    if (right) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      const rightLabelLines = doc.splitTextToSize(`${right.label}:`, rightColumnLabelWidth);
      const rightVal = formatValue(right.value);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      const rightValLines = doc.splitTextToSize(rightVal, maxValueWidthTwoColumn);
      rightHeight = Math.max(rightLabelLines.length, rightValLines.length) * lineHeight;
    }

    const maxRowHeight = Math.max(leftHeight, rightHeight);
    checkPageBreak(maxRowHeight + 4);

    const startY = yPos;

    if (left) {
      renderRow(left.label, left.value, leftColumnLabelX, leftColumnValueX, leftColumnLabelWidth, maxValueWidthTwoColumn);
    }

    yPos = startY;

    if (right) {
      renderRow(right.label, right.value, rightColumnLabelX, rightColumnValueX, rightColumnLabelWidth, maxValueWidthTwoColumn);
    }

    yPos = startY + maxRowHeight + 4;
  }

  // Single-column layout for long labels
  longPairs.forEach((p) => {
    const height = renderRow(p.label, p.value, singleColumnLabelX, singleColumnValueX, singleColumnLabelWidth, singleColumnValueWidth);
    yPos += height + 4;
  });

  yPos += 2;
};

// Also fix the formatValue function to handle boolean values better:
const formatValue = (value: any) => {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "object" && value !== null) {
    // Handle object with value property (like verified fields)
    if ("value" in value) {
      return value.value || "—";
    }
    // Don't stringify objects, just show empty
    return "—";
  }
  return String(value);
};

// Also update the addField function to properly handle multiline text:
const addField = (label: string, value: any, xPos = 110) => {
  const val = formatValue(value);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  const labelLines = doc.splitTextToSize(`${label}:`, 90);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const valueLines = doc.splitTextToSize(val, 90);

  // Calculate total height needed
  const labelHeight = labelLines.length * 6;
  const valueHeight = valueLines.length * 6;
  const neededHeight = Math.max(labelHeight, valueHeight);

  // Check if we have space for all lines
  checkPageBreak(neededHeight + 2);

  const startY = yPos;

  // Draw label lines
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  let currentY = startY;
  for (let i = 0; i < labelLines.length; i++) {
    doc.text(labelLines[i], 15, currentY);
    currentY += 6;
  }

  // Draw value lines (aligned to start)
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  currentY = startY;
  for (let i = 0; i < valueLines.length; i++) {
    doc.text(valueLines[i], xPos, currentY);
    currentY += 6;
  }

  yPos = startY + neededHeight + 2; // Move yPos to after the tallest content
};

  // ==================== SECTION-SPECIFIC RENDER FUNCTIONS ====================

const renderArrayAsSmartFields = (
  title: string,
  array: any[],
  fieldDefs: { label: string; key: string; fallbackKey?: string }[],
  prefix = "",
) => {
  const filteredArray = cleanRows(array || []);
  if (filteredArray.length === 0) return;

  filteredArray.forEach((item: any, idx: number) => {
    const pairs = fieldDefs
      .map((def) => ({
        label: def.label,
        value:
          item?.[def.key] !== undefined && item?.[def.key] !== null
            ? item?.[def.key]
            : def.fallbackKey
              ? item?.[def.fallbackKey]
              : undefined,
      }))
      .filter(
        (p) =>
          p.value !== undefined &&
          p.value !== false &&
          p.value !== "" &&
          p.value !== null &&
          (typeof p.value !== "string" || p.value.trim() !== ""),
      );

    if (pairs.length === 0) return;

    const subTitle = filteredArray.length > 1 ? `${title} ${idx + 1}` : title;
    
    // Add spacing before new subsection
    checkPageBreak(12);
    
    if (prefix) {
      addSubSection(`${prefix} ${subTitle}`);
    } else {
      addSubSection(subTitle);
    }

    // Add some spacing before fields
    checkPageBreak(4);
    
    addSmartFields(pairs);
    
    // Add spacing after fields
    yPos += 8;
  });
};

  // ==================== RENDER SECTIONS (with filtering) ====================

  // 1. General Verification
  if (isSectionMeaningful("generalVerification")) {
    addSectionTitle(
      "General Verification",
      parsed.generalVerification?.bgColor,
    );

    // Filter fields based on meaningful values
    const generalFields = [
      {
        label: "Title Owner/ Property Owner Name Verified with Aadharcard Name?",
        value: parsed?.generalVerification?.ownerAadharName?.verified,
      },
      {
        label: "Borrower Name Verified with Aadharcard Name?",
        value: parsed?.generalVerification?.borrowerAadharName?.verified,
      },
      {
        label: "Co-Borrower Name / Co-Aplicant Name Verified with AadharCard Name?",
        value: parsed?.generalVerification?.coBorrowerAadharName?.verified,
      },
      {
        label: "Is The Spelling for Mr./Smt/Minor/Late/ Initial  Correct?",
        value: parsed?.generalVerification?.isSpellingCorrect,
      },
      {
        label: "Address of the Property - Hint about File Verified",
        value: parsed?.generalVerification?.addressofProperty?.verified,
      },
      {
        label: "Nature of the Property (Residential/Commercial/Agri/Industrial)",
        value: parsed?.generalVerification?.natureofProperty?.verified,
      },
      { label: "APP ID Verified", value: parsed?.generalVerification?.AppIdverfy?.verified },
      { label: "Ownership", value: parsed?.generalVerification?.ownerShip },
      {
        label: "Hint About File Verified",
        value: parsed?.generalVerification?.hintAbtfile?.verified,
      },
      {
        label: "Freehold/ LeaseHold",
        value: parsed?.generalVerification?.freeRleasehold?.verified,
      },
    ].filter(p => isMeaningfulValue(p.value));

    addSmartFields(generalFields);

    const file = parsed.generalVerification?.file;
    if (file && Object.values(file).some(isMeaningfulValue)) {
      addSubSection("File");

      const filePairs = [
        { label: "BT Transfer", value: file.btTransfer },
        { label: "MOD Available", value: file.modAvailable },
        { label: "Purchase", value: file.purchase },
        { label: "Sale Agreement", value: file.saleAgreement },
        {
          label: "New Building / Construction/Self Construction",
          value: file.selfConstruction,
        },
        {
          label: "Proceeding Approval/Approved Plan",
          value: file.proceedingApproval,
        },
        { label: "Fresh Case", value: file.new },
        { label: "Pre disbursement Verified", value: file.preDisbursmentverfy },
        { label: "Post Disbursement Verified", value: file.postDisbursverfy },
      ].filter((p) => isMeaningfulValue(p.value));

      addSmartFields(filePairs);
    }
  }

  // 2. HSD Pattas Verification
  if (isSectionMeaningful("HsdpattasVerify")) {
    addSectionTitle(
      "HSD Patta Verification ( Title Deed)/Assignment Patta Verification",
      parsed.HsdpattasVerify?.bgColor,
    );

    const filteredPattas = cleanRows(parsed.HsdpattasVerify?.Hsdpattas || []);
    filteredPattas.forEach((patta: any, idx: number) => {
      const pairs = [
        { label: "Condition Fulfilled", value: patta.conditionFullfilled },
        { label: "NOC for Tahsildar", value: patta.nocforTahsildar },
        { label: "HSD Patta Issued by", value: patta.hsdpattaissuedby },
        { label: "Mention Condition", value: patta.mentionCondition },
        { label: "Boundary Certificate", value: patta.certificate },
        { label: "Road Approaches", value: patta.roadApproachs },
      ].filter(p => isMeaningfulValue(p.value));

      if (pairs.length > 0) {
        addSubSection(`HSD Patta ${idx + 1}`);
        addSmartFields(pairs);
      }
    });
  }

  // 3. Natham Patta (Title Deed)
  if (isSectionMeaningful("nathamPattaverify")) {
    addSectionTitle(
      "Natham Patta (Title Deed)",
      parsed.nathamPattaverify?.bgColor,
    );

    const filteredPattas = cleanRows(parsed.nathamPattaverify?.nathamPatta || []);
    filteredPattas.forEach((patta: any, idx: number) => {
      const pairs = [
        { label: "Issued By Special Tahsildar", value: patta.specialTahsildar },
        { label: "Boundary Certificate", value: patta.boundaryCertificate },
        { label: "Road Approach", value: patta.roadApproach },
      ].filter(p => isMeaningfulValue(p.value));

      if (pairs.length > 0) {
        addSubSection(`Natham Patta ${idx + 1}`);
        addSmartFields(pairs);
      }
    });
  }

  // 4. Flow
  if (isSectionMeaningful("flow")) {
    addSectionTitle("Flow", parsed.flow?.bgColor);

    const filteredFlows = cleanRows(parsed.flow?.flow || []);
    filteredFlows.forEach((flow: any, idx: number) => {
      const pairs = [
        {
          label: "Chainlink is in Order- Favour and Excecutant is in flow",
          value: flow.Chainlink,
        },
        {
          label:
            "in flow ,document holder expired means Legal Heir Certificate available",
          value: flow.inFlowdoc,
        },
        {
          label: "All the Legal Heirs signed in the next document",
          value: flow.legalHeirsSigndoc,
        },
        {
          label: "In flow , Court order comes -Verified",
          value: flow.courtOrderVerify,
        },
        { label: "In flow, POA Verified", value: flow.flowPoAVerfy },
        {
          label:
            "In flow Extent compare with the origin document to title deed",
          value: flow.flowextentDeed,
        },
        {
          label:
            "In flow Survey Number compare with the origin document to title deed",
          value: flow.flowSurveynoDeed,
        },
      ].filter(p => isMeaningfulValue(p.value));

      if (pairs.length > 0) {
        addSubSection(`Flow ${idx + 1}`);
        addSmartFields(pairs);
      }
    });
  }

  // 5. Property Tax
  if (isSectionMeaningful("propertytax")) {
    addSectionTitle("Property Tax", parsed.propertytax?.bgColor);

    const filteredTaxes = cleanRows(parsed.propertytax?.propertytax || []);
    filteredTaxes.forEach((propertytax: any, idx: number) => {
      const pairs = [
        { label: "Date Verified", value: propertytax.dataVerified },
        {
          label: "Property Owner Name Verified",
          value: propertytax.propertOwnerVerify,
        },
        { label: "Amount  Verified", value: propertytax.propertyAmountverify },
        { label: "Door.No Verified", value: propertytax.doorNoVerify },
        {
          label: "Assessment No Verified",
          value: propertytax.assessmentVerifi,
        },
        {
          label: "Property Tax issued by Verified",
          value: propertytax.propertyTaxVerify,
        },
        {
          label: "Property Tax Verified with QR Code",
          value: propertytax.propertyTaxVerifyQrcode,
        },
        {
          label: "Attached Proof Submitted for Verified QR Code",
          value: propertytax.attachProofVerifyQrcode,
        },
      ].filter(p => isMeaningfulValue(p.value));

      if (pairs.length > 0) {
        addSubSection(`Property Tax ${idx + 1}`);
        addSmartFields(pairs);
      }
    });
  }

  // 6. EB Receipt
  if (isSectionMeaningful("EBreceiptData")) {
    addSectionTitle("EB Receipt", parsed.EBreceiptData?.bgColor);

    const filteredReceipts = cleanRows(parsed.EBreceiptData?.EBreceipt || []);
    filteredReceipts.forEach((patta: any, idx: number) => {
      const pairs = [
        { label: "Date Verified", value: patta.dataVerified },
        {
          label: "Property Owner Name Verified",
          value: patta.propertOwnerVerify,
        },
        { label: "Amount  Verified", value: patta.ebAmountverify },
        {
          label: "EB Receipt issued by Verified",
          value: patta.receiptIssuedByVerify,
        },
      ].filter(p => isMeaningfulValue(p.value));

      if (pairs.length > 0) {
        addSubSection(`EB Receipt ${idx + 1}`);
        addSmartFields(pairs);
      }
    });
  }

  // 7. Survey No Verification
  if (isSectionMeaningful("surveyNoVerification")) {
    addSectionTitle(
      "Survey No Verification",
      parsed.surveyNoVerification?.bgColor,
    );

    // Check and render each subsection based on show status
    if (shouldShowSubSection(parsed.surveyNoVerification, "isshowregisterStatus")) {
      const filteredRegister = cleanRows(parsed.surveyNoVerification?.register || []);
      if (filteredRegister.length > 0) {
        addSubSection("A Register From Patta");
        filteredRegister.forEach((item: any, idx: number) => {
          const pairs = [
            { label: "Survey No", value: item?.surveyNo },
            { label: "TSLR No", value: item?.tslrNo },
            { label: "Extent", value: item?.extent },
            { label: "Name", value: item?.name },
            {
              label:
                "Origin A Register Means Compare with Title Deed Survey Number",
              value: item?.aRegisterMeanscompare,
            },
          ].filter(p => isMeaningfulValue(p.value));

          if (pairs.length > 0) {
            const subTitle = filteredRegister.length > 1
              ? `Register Entry ${idx + 1}`
              : "Register Entry";
            addSubSection(subTitle);
            addSmartFields(pairs);
          }
        });
      }
    }

    if (shouldShowSubSection(parsed.surveyNoVerification, "isshowtitleDeedStatus")) {
      const filteredTitleDeed = cleanRows(parsed.surveyNoVerification?.titleDeed || []);
      if (filteredTitleDeed.length > 0) {
        addSubSection("Title Deed");
        filteredTitleDeed.forEach((item: any, idx: number) => {
          // Only include meaningful fields (similar to table columns in PreviewChecklist)
          const pairs = [
            { label: "New Survey Number Verified", value: item?.surveyNo },
            {
              label: "New Survey Number Verified with Patta Extract",
              value: item?.tslrNo,
            },
            {
              label: "Patta Extract S.No",
              value: item?.pattaExtractSno,
            },
            {
              label: "Title Deed Extent (Sq.Mt, Sq.Ft ,Cents) Verified",
              value: item?.extent,
            },
            { label: "Name", value: item?.name },
            {
              label: "Corelation Certificate Verified",
              value: item?.Corelation,
            },
            {
              label: "Proceeding of Patta Verified",
              value: item?.proceedingpatta,
            },
            {
              label: "Title Deed Extent Verified With Patta Extract",
              value: item?.deedExtentverfy,
            },
            {
              label: "Road Approach Available in Boundaries",
              value: item?.roadAppBound,
            },
            {
              label: "Pathway Available in Boundaries",
              value: item?.pathwayBound,
            },
            {
              label: "Of /BY Boundaries direction Verified",
              value: item?.ofbyBoundverfy,
            },
            {
              label: "Boundary of North Verified",
              value: item?.boundverfyNorth,
            },
            {
              label: "Boundary of South Verified",
              value: item?.boundverfySouth,
            },
            { label: "Boundary of East Verified", value: item?.boundverfyEast },
            { label: "Boundary of West Verified", value: item?.boundverfyWest },
            {
              label: "North-East West Measureements in Sq.Ft/ Sq.Mt",
              value: item?.northeastMeasur,
            },
            {
              label: "South-East West Measurements in Sq.Ft/ Sq.Mt",
              value: item?.southeastMeasur,
            },
            {
              label: "East-North South Measurements In Sq.Ft/ Sq.Mt",
              value: item?.eastnorthMeasur,
            },
            {
              label: "West-North South Measurements Sq.Ft/ Sq.Mt",
              value: item?.westnorthMeasur,
            },
            {
              label: "Measurement Extent Matched with Title Deed Total Extent",
              value: item?.measurementextentmatch,
            },
            {
              label: "Land type is Nanjai (Yes Means Hold/Policy/Approval)",
              value: item?.landTypeNanjai,
            },
            {
              label:
                "Is Title Extent more than 20 Cents (Non Agri From Tahsildar)",
              value: item?.NonAgrifromtashidar,
            },
            { label: "Door No Verified", value: item?.deedDoorNoverfy },
            { label: "Plot No Verified", value: item?.deedplotNoverfy },
            { label: "Layout Name Verified", value: item?.deedlayoutnameverfy },
            { label: "Street Name Verified", value: item?.deedstreetnameverfy },
            {
              label: "New Taluk Verified with Patta",
              value: item?.newtalukVerified,
            },
          ].filter(p => isMeaningfulValue(p.value));

          if (pairs.length > 0) {
            const subTitle = filteredTitleDeed.length > 1
              ? `Title Deed ${idx + 1}`
              : "Title Deed";
            addSubSection(subTitle);
            addSmartFields(pairs);
          }
        });
      }
    }

    if (shouldShowSubSection(parsed.surveyNoVerification, "isshowcurrentPattaStatus")) {
      const filteredCurrentPatta = cleanRows(parsed.surveyNoVerification?.currentPatta || []);
      if (filteredCurrentPatta.length > 0) {
        addSubSection("Current Patta/Chitta/TSLR No");
        filteredCurrentPatta.forEach((item: any, idx: number) => {
          const pairs = [
            { label: "Survey No", value: item?.surveyNo },
            { label: "TSLR No", value: item?.tslrNo },
            { label: "Extent", value: item?.extent },
            { label: "Name", value: item?.name },
            {
              label: "Government Land or Not-Verified",
              value: item?.governmentLandorNotverify,
            },
          ].filter(p => isMeaningfulValue(p.value));

          if (pairs.length > 0) {
            const subTitle = filteredCurrentPatta.length > 1
              ? `Current Patta/Chitta/TSLR No ${idx + 1}`
              : "Current Patta/Chitta/TSLR No";
            addSubSection(subTitle);
            addSmartFields(pairs);
          }
        });
      }
    }

    // Other fields that don't have show status
    const otherFields = [
      {
        label: "Total Survey No in Title Deed",
        value: parsed.surveyNoVerification?.totalTitleDeed,
      },
      {
        label: "Total Survey No in EC",
        value: parsed.surveyNoVerification?.totalEc,
      },
      {
        label: "Patta Verified with QR Code",
        value: parsed.surveyNoVerification?.pattaqrcode,
      },
      {
        label: "Attachment Proof Submitted for Verified QR Code",
        value: parsed.surveyNoVerification?.proofverifyqrcode,
      },
      {
        label: "Patta Issued by Verified",
        value: parsed.surveyNoVerification?.pattaissuedverfy,
      },
      { label: "Difference", value: parsed.surveyNoVerification?.difference },
      {
        label: "Missing Survey No in EC",
        value: parsed.surveyNoVerification?.missingEc,
      },
      {
        label: "Land Type  (Yes Means Hold/Policy/Approval)",
        value: parsed.surveyNoVerification?.type ? "Nanjai" : "Punjai",
      },
      {
        label: "Condition Patta",
        value: parsed.surveyNoVerification?.conditionPatta,
      },
    ].filter(p => isMeaningfulValue(p.value));

    if (otherFields.length > 0) {
      addSmartFields(otherFields);
    }
  }

  // 8. Deed
  if (isSectionMeaningful("deed")) {
    addSectionTitle("Deed", parsed.deed?.bgColor);

    // Check show status for each deed type before rendering
    if (shouldShowSubSection(parsed.deed, "isshowsaleDeedStatus")) {
      renderArrayAsSmartFields("Sale Deed", parsed.deed?.saleDeed || [], [
        { label: "Amount Fully Transferred?", key: "saleDeedAmountTransfered" },
        {
          label: "Conditions Mentioned or Not?",
          key: "saleDeedConditionsMentioned",
        },
      ]);
    }

    if (shouldShowSubSection(parsed.deed, "isshowgiftDeedStatus")) {
      renderArrayAsSmartFields("Gift Settlement Deed", parsed.deed?.giftDeed || [], [
        { label: "Derived By", key: "derivedBy" },
        { label: "Registered?", key: "registered" },
        { label: "Condition?", key: "condition" },
        { label: "Condition Fulfilled?", key: "conditionFulfilled" },
        { label: "Mention Condition", key: "mentionCondition" },
      ]);
    }

    if (shouldShowSubSection(parsed.deed, "isshowsettlementDeedStatus")) {
      renderArrayAsSmartFields(
        "Settlement Deed",
        parsed.deed?.settlementDeed || [],
        [
          { label: "Derived By", key: "derivedBy" },
          { label: "Registered?", key: "registered" },
          { label: "Condition?", key: "condition" },
          { label: "Condition Fulfilled?", key: "conditionFulfilled" },
          {
            label: "Death Certificate Obtained?",
            key: "deathcertificateobtained",
          },
          { label: "Relationship Mentioned?", key: "relationshipMentioned" },
          { label: "Mention Condition", key: "mentionCondition" },
        ],
      );
    }

    if (shouldShowSubSection(parsed.deed, "isshowpartitionDeedStatus")) {
      renderArrayAsSmartFields(
        "Partition Release Deed",
        parsed.deed?.partitionDeed || [],
        [
          { label: "Derived By", key: "derivedBy" },
          { label: "Registered?", key: "registered" },
          { label: "Condition?", key: "condition" },
          { label: "Condition Fulfilled?", key: "conditionFulfilled" },
          { label: "Mention Condition", key: "mentionCondition" },
          {
            label: "Doc Alloted For Mortgage",
            key: "partionscheduleMortage",
            fallbackKey: "docAllotedForMortgage",
          },
          { label: "Original Copy Available", key: "originalCopyAvailable" },
        ],
      );
    }

    if (shouldShowSubSection(parsed.deed, "isshowwillDeedStatus")) {
      renderArrayAsSmartFields("Will Deed", parsed.deed?.willDeed || [], [
        { label: "Derived By", key: "derivedBy" },
        { label: "Registered?", key: "registered" },
        { label: "Condition?", key: "condition" },
        { label: "Condition Fulfilled?", key: "conditionFulfilled" },
        { label: "Mention Condition", key: "mentionCondition" },
        { label: "Cerified Copy Verified", key: "willdeedcertcopyverify" },
        { label: "Death Certificate Obtained", key: "willdeaddeathcert" },
        { label: "Relationship Mentioned", key: "willdeadreationship" },
      ]);
    }

    if (shouldShowSubSection(parsed.deed, "isshowleaseDeedStatus")) {
      renderArrayAsSmartFields("Lease Deed", parsed.deed?.leaseDeed || [], [
        { label: "Lease Period From Date", key: "leasePeriodFromDate" },
        { label: "Lease Period To Date", key: "leasePeriodToDate" },
        { label: "NOC Available?", key: "nocAvailable" },
        {
          label: "Is there a word  to empower mortgage in Lease Deed?",
          key: "leaseDeedempowermortage",
        },
        { label: "Lease Cancellation Available?", key: "leaseDeedCancellation" },
      ]);
    }

    if (shouldShowSubSection(parsed.deed, "isshowattorneyDeedStatus")) {
      renderArrayAsSmartFields("Attorney Deed", parsed.deed?.attorneydeed || [], [
        { label: "Derived By", key: "derivedBy" },
        { label: "Registered?", key: "registered" },
        { label: "Is In Force?", key: "isInForce" },
        {
          label: "Is Certified Copy Available?",
          key: "isCertifiedCopyAvailable",
        },
        {
          label: "Is there a word  to empower mortgage in power Document?",
          key: "attorneyDeedmortage",
        },
        {
          label: "Sale Receipt -Subsequent Deed available or not?",
          key: "attorneyDeedsaleReceipt",
        },
        {
          label:
            "Life Certificate is available for current Transfer(30 days Validity)",
          key: "attorneyLifecert",
        },
      ]);
    }

    if (shouldShowSubSection(parsed.deed, "isshowreleaseDeedStatus")) {
      renderArrayAsSmartFields("Release Deed", parsed.deed?.releaseDeed || [], [
        { label: "Derived By", key: "derivedBy" },
        { label: "Registered?", key: "registered" },
        { label: "Condition?", key: "condition" },
        { label: "Fulfilled?", key: "fulFilled" },
        { label: "Property Share", key: "propertyshare" },
        { label: "Mention Condition", key: "mentionCondition" },
        { label: "Death Certificate Obtained?", key: "deatcertficate" },
        { label: "Relationship Mentioned?", key: "relationShipmentioned" },
      ]);
    }

    if (shouldShowSubSection(parsed.deed, "isshowrectificationDeedStatus")) {
      renderArrayAsSmartFields(
        "Rectification Deed",
        parsed.deed?.rectificationDeed || [],
        [
          { label: "Derived By", key: "derivedBy" },
          { label: "Registered?", key: "registered" },
          { label: "Wrong Mentioned Details", key: "mentionDetails" },
          { label: "Correct Details", key: "correctDetails" },
        ],
      );
    }

    if (shouldShowSubSection(parsed.deed, "isshowconsentDeedStatus")) {
      renderArrayAsSmartFields("Consent Deed", parsed.deed?.consentDeed || [], [
        { label: "Derived By", key: "derivedBy" },
        { label: "Registered?", key: "registered" },
        { label: "Reason", key: "reason" },
        { label: "Age checked with Aadharcard", key: "agecheckAadhar" },
      ]);
    }

    if (shouldShowSubSection(parsed.deed, "isshowmissingDocumentStatus")) {
      renderArrayAsSmartFields(
        "Missing Document",
        parsed.deed?.missingDocument || [],
        [
          { label: "Police Complaint", key: "policeComplaint" },
          { label: "Paper Ad (Tamil/ English)", key: "paperAd" },
          {
            label: "Non Tracable certificate by Police",
            key: "tracableCertificate",
          },
          {
            label: "No Claim Certificate by Advocate",
            key: "NoClaimcertificate",
          },
        ],
      );
    }

    // Plot Regularization
    if (shouldShowSubSection(parsed.deed, "ishowplotregulationstatus") &&
        parsed.deed?.plotRegularization &&
        (parsed.deed.plotRegularization.isDeedRegisteredAfter30_9_2016 === true ||
         parsed.deed.plotRegularization.isPlotObtained === true)) {
      addSubSection("Plot Regularization");
      addSmartFields([
        {
          label: "Is Deed Registered After 30-09-2016?",
          value: parsed.deed.plotRegularization.isDeedRegisteredAfter30_9_2016,
        },
        {
          label: "Is Plot Regulation Obtained?",
          value: parsed.deed.plotRegularization.isPlotObtained,
        },
      ]);
    }
    // Gazette
    if (shouldShowSubSection(parsed.deed, "ishowGazettestatus") &&
        parsed.deed?.gazetteofDoc &&
        (
          parsed.deed.gazetteofDoc.isNameChanged === true ||
          parsed.deed.gazetteofDoc.gazettePublication === true ||
          parsed.deed.gazetteofDoc.NameChangedbythasildar === true ||
          parsed.deed.gazetteofDoc.IndementyBond === true ||
         parsed.deed.gazetteofDoc.docList !== "")) {
      addSubSection("Gazette");
      addSmartFields([
        {
          label: "Is Name Changed(Gazette)",
          value: parsed.deed.gazetteofDoc.isNameChanged,
        },
        {
          label: "Gazette Publication available?",
          value: parsed.deed.gazetteofDoc.gazettePublication,
        },
        {
          label: "Name Changed Certifcate by Tahsildar/VAO?",
          value: parsed.deed.gazetteofDoc.NameChangedbythasildar,
        },
        {
          label: "Indementy Bond/Affidavit?",
          value: parsed.deed.gazetteofDoc.IndementyBond,
        },
        {
          label: "Name Changed Document(s) List?",
          value: parsed.deed.gazetteofDoc.docList,
        },
      ]);
    }

    // Adjudication Power Deed
    if (shouldShowSubSection(parsed.deed, "ishowAdjudicationstatus") &&
        parsed.deed?.foriegnPower &&
        (parsed.deed.foriegnPower.isForiegnPowerRegistered === true ||
         isMeaningfulValue(parsed.deed.foriegnPower.docNo))) {
      addSubSection("Adjudication Power Deed");
      const fields = [
        {
          label: "Is Adjudication Power Registered?",
          value: parsed.deed.foriegnPower.isForiegnPowerRegistered,
        },
        {
          label: "Doc No",
          value: parsed.deed.foriegnPower.docNo,
        },
      ].filter(f => isMeaningfulValue(f.value));
      addSmartFields(fields);
    }

    // Mortgage in Power Doc
    if (parsed.deed?.mortgageInPowerDoc === true) {
      addSmartFields([{
        label: "Is there a word to Empowerr to Mortgage",
        value: parsed.deed.mortgageInPowerDoc,
      }]);
    }
  }

  // 9. Legal Heirs Verification
  if (isSectionMeaningful("legalHeirsVerify")) {
    addSectionTitle(
      "Legal Heirs Verification",
      parsed.legalHeirsVerify?.bgColor,
    );

    const filteredHeirs = cleanRows(parsed.legalHeirsVerify?.legalHeirs || []);
    filteredHeirs.forEach((heir: any, idx: number) => {
      const pairs = [
        {
          label: "Legal Heirs Certificate Number & Year",
          value: heir.docNoAndYear,
        },
        { label: "Next Deed Doc No & Year", value: heir.nextDeedDocNoAndYear },
        {
          label: "Predeceased Son/Daughter Legal Heirs Verified",
          value: heir.PredeceasedlegalHeire,
        },
        {
          label: "Legal Heir Certificate Verified with QR Code",
          value: heir.legalHeirsWithqrcode,
        },
        {
          label: "Attached Proof Submitted for Verified QR Code",
          value: heir.legalHeirsattacedproof,
        },
        { label: "Legal Heirs Obtained From", value: heir.legalHeirsObtained },
        {
          label: "Genealogy Obtained from",
          value: heir.genealogy ?? heir.geanology,
        },
      ].filter(p => isMeaningfulValue(p.value));

      if (pairs.length === 0) return;

      addSubSection(`Legal Heir ${idx + 1}`);
      
      // Add the main field separately
      if (heir.legalHeirsWithin13 === true) {
        addField("Legal Heirs Within 13 Years Period?", heir.legalHeirsWithin13);
      }
      
      addSmartFields(pairs);
    });
  }

  // 10. EC Verification
  if (isSectionMeaningful("ecVerify")) {
    addSectionTitle("EC Verification", parsed.ecVerify?.bgColor);

    const filteredECEntries = cleanRows(parsed.ecVerify?.ecentry || []);
    filteredECEntries.forEach((entry: any, idx: number) => {
      const pairs = [
        { label: "No Of EC Entries", value: entry.noOfEcEntries },
        { label: "Related EC Entries", value: entry.relatedEcEntries },
        { label: "Not Related Verified?", value: entry.notRelatedVerified },
        {
          label: "SRO EC Verified with QR Code",
          value: entry.onlineEcVerified,
        },
        { label: "Court Attachment", value: entry.isCourtAttachment },
        { label: "Attachment Canceled", value: entry.isAttachmentCanceled },
        { label: "Lease In EC", value: entry.leaseInEc },
        { label: "Lease Cancellation", value: entry.leaseCancellation },
        { label: "Mortgage In EC", value: entry.isMortgageInEc },
        { label: "Receipt In EC", value: entry.isReceiptInEc },
        { label: "Agreement In EC", value: entry.isAgreementInEc },
        { label: "Agreement Canceled", value: entry.isAgreementCanceled },
        { label: "Larger Extent", value: entry.largerExtent },
        { label: "Non Related S No", value: entry.nonRelatedSNo },
        { label: "Different Plot No", value: entry.differentPlotNo },
        {
          label: "Property Details (S.No / Extent / Plot No) Verified",
          value: entry?.propertdetailsverfy,
        },
        {
          label: "Period Continuity upto Date Verified",
          value: entry?.periodcontinuityverfy,
        },
        {
          label: "Attached Proof Submitted for Verified QR Code",
          value: entry?.attcheProofVerifyQrcode,
        },
        {
          label:
            "Same Plot No / Same Extent / Same S.No / or Title Holder or Previous Title Holder found in EC entries",
          value: entry?.holderECentries,
        },
        { label: "Any Index Error Found", value: entry?.indexerrorfound },
        {
          label: "Double Entry (Document Number / Plot No / Extent)",
          value: entry?.doubleEntry,
        },
        { label: "EC Page Missing Verified", value: entry?.ecPageMIssing },
      ].filter(p => isMeaningfulValue(p.value));

      if (pairs.length === 0) return;

      addSubSection(`EC Entry ${idx + 1}`);
      addSmartFields(pairs);
    });
  }

  // 11. Missing Sheets in LOD
  if (isSectionMeaningful("missingLOD")) {
    addSectionTitle("Missing Sheets in LOD", parsed.missingLOD?.bgColor);

    const filteredSheets = cleanRows(parsed.missingLOD?.sheets || []);
    if (filteredSheets.length > 0) {
      addSubSection("Missing Sheets Details");
      filteredSheets.forEach((sheet: any, idx: number) => {
        const pairs = [
          { label: "Deed Name", value: sheet?.fileName },
          { label: "Doc No", value: sheet?.fileNo },
          { label: "Total Sheets", value: sheet?.totalSheets },
          { label: "Available Sheets", value: sheet?.availableSheets },
          { label: "Missing Sheets", value: sheet?.missingSheets },
          {
            label: "Certificate Copy Attached",
            value: sheet?.isCertificateAttached ? "Yes" : "No",
          },
          {
            label: "Gazette Publication available",
            value: sheet?.gazettePublication ? "Yes" : "No",
          },
          {
            label: "Name Changed Certificate by Tahsildar/VAO",
            value: sheet?.NameChangedbythasildar ? "Yes" : "No",
          },
          {
            label: "Indemnity Bond/Affidavit",
            value: sheet?.IndementyBond ? "Yes" : "No",
          },
        ].filter(p => isMeaningfulValue(p.value));

        if (pairs.length > 0) {
          const subTitle = filteredSheets.length > 1 ? `Sheet ${idx + 1}` : "Sheet";
          addSubSection(subTitle);
          addSmartFields(pairs);
        }
      });
    }

    const finalPairs = [
      {
        label: "Is Name Changed(Gazette)?",
        value: parsed.missingLOD?.isNameChanged,
      },
      {
        label: "Name Changed Document(s) List?",
        value: parsed.missingLOD?.docList,
      },
    ].filter(p => isMeaningfulValue(p.value));

    if (finalPairs.length > 0) {
      addSmartFields(finalPairs);
    }
  }

  // 12. Verification
  if (isSectionMeaningful("verification")) {
    addSectionTitle("Verification", parsed.verification?.bgColor);

    const renderEntity = (
      title: string,
      entity: any,
      fields: { label: string; key: string }[],
      showStatus: boolean
    ) => {
      if (!showStatus || !entity) return;
      
      const pairs = fields
        .map((f) => ({ label: f.label, value: entity[f.key] }))
        .filter(p => isMeaningfulValue(p.value));

      if (pairs.length === 0) return;

      addSubSection(title);
      addSmartFields(pairs);
    };

    // Check show status for each verification type
    renderEntity(
      "Company",
      parsed.verification?.company,
      [
        { label: "Board Resolution", key: "boardResolution" },
        { label: "All Directors Signed In?", key: "allDirectorsSignedIn" },
        { label: "Mortage Permit  in MOA & AOA ", key: "mortagepermit" },
        { label: "MOA & AOA ", key: "MoaAndAoa" },
        { label: "Cert of Incorporation Available", key: "certOfInc" },
      ],
      shouldShowSubSection(parsed.verification, "isshowcompanyStatus")
    );

    renderEntity(
      "Trust",
      parsed.verification?.trust,
      [
        { label: "Trust Deed Available", key: "isDeed" },
        { label: "Supplimentary Trust Deed Available", key: "isSupplement" },
        { label: "Board of Resolution Signed?", key: "isResolutionSigned" },
        {
          label: "Affidavit from all the Trustees Signed?",
          key: "isAffidavitSigned",
        },
      ],
      shouldShowSubSection(parsed.verification, "isshowtrustStatus")
    );

    renderEntity(
      "Partnership",
      parsed.verification?.partnership,
      [
        { label: " Partnership Deed Available", key: "isPartnershipDeed" },
        {
          label: "Supplimentary Partnership Deed Available",
          key: "issupplimentaryPartnershipDeed",
        },
        {
          label: "Is there a word to Empower to Mortgage",
          key: "wordtoempowerrmortage",
        },
        {
          label: "Letter Obtained from Partners to Mortgage",
          key: "letterObtainpartenermortage",
        },
        { label: "Form C", key: "isFormC" },
        { label: "Retire Partner", key: "isRetirePartner" },
        { label: "Add Of Partner", key: "addOfPartner" },
        { label: "Letter Obtained", key: "letterObtained" },
        { label: "Single Partner In Deed", key: "singlePartnerInDeed" },
      ],
      shouldShowSubSection(parsed.verification, "isshowpartnershipStatus")
    );

    renderEntity(
      "Association",
      parsed.verification?.association,
      [
        { label: "Association Law", key: "isAssociationLaw" },
        { label: "Board of Resolution", key: "isResolution" },
        { label: "Power To Sign Execution", key: "powerToSignExecution" },
      ],
      shouldShowSubSection(parsed.verification, "isshowassociationStatus")
    );

    renderEntity(
      "Society",
      parsed.verification?.society,
      [
        { label: "MOA", key: "isMOA" },
        { label: "Society Registered", key: "isSocietyRegistered" },
        {
          label: "Board of Resolution Society Signed?",
          key: "isResolutionSocietySigned",
        },
        { label: "Bylaw of Society?", key: "isBylaw" },
        { label: "Power to Mortgage and Sale?", key: "isMortagesale" },
      ],
      shouldShowSubSection(parsed.verification, "isshowsocietyStatus")
    );

    renderEntity(
      "Apartment",
      parsed.verification?.appartment,
      [
        { label: "UDS", key: "isUDS" },
        { label: "Building Floor", key: "isBuildingFloor" },
        { label: "Extent", key: "isExtent" },
      ],
      shouldShowSubSection(parsed.verification, "isshowappartmentStatus")
    );
  }

  // 13. Court Document Verification
  if (isSectionMeaningful("courtDocVerification")) {
    addSectionTitle(
      "Court Document Verification",
      parsed.courtDocVerification?.bgColor,
    );

    const renderCourt = (
      title: string,
      data: any,
      fields: { label: string; key: string }[],
      showStatus: boolean
    ) => {
      if (!showStatus || !data) return;

      const pairs = fields
        .map((f) => ({ label: f.label, value: data[f.key] }))
        .filter(p => isMeaningfulValue(p.value));

      if (pairs.length === 0) return;

      addSubSection(title);
      addSmartFields(pairs);
    };

    // Check show status for each court document type
    renderCourt(
      "Partition Suit",
      parsed.courtDocVerification?.partitionSuit,
      [
        { label: "Final Decree", key: "isFinalDecree" },
        { label: "Preliminary Decree", key: "isPreliminaryDecree" },
        { label: "Compromise Decree", key: "isCompormiseDecree" },
        { label: "Exparty Decree", key: "isExpartyDecree" },
        { label: "Contested Decree", key: "ContestedDecree" },
      ],
      shouldShowSubSection(parsed.courtDocVerification, "isshowPartitionSuitStatus")
    );

    renderCourt(
      "Declaration Suit",
      parsed.courtDocVerification?.declarationSuit,
      [
        { label: "Is IA Available?", key: "isIAAvailable" },
        { label: "Contest", key: "isContest" },
        { label: "Compromise Decree", key: "isCompormiseDecree" },
        { label: "Exparty Decree", key: "isExpartyDecree" },
      ],
      shouldShowSubSection(parsed.courtDocVerification, "isshowDeclarationSuitStatus")
    );

    renderCourt(
      "Lok Adalat",
      parsed.courtDocVerification?.lokAdalat,
      [
        { label: "Old Court Name", key: "oldCourtName" },
        { label: "Case No", key: "caseNo" },
        { label: "Lok Adalat Case No", key: "lokAdalatCaseNo" },
      ],
      shouldShowSubSection(parsed.courtDocVerification, "isshowLokAdalatStatus")
    );

    renderCourt(
      "Specific Performance Suit",
      parsed.courtDocVerification?.specificSuit,
      [
        { label: "Agreement Holder Name", key: "agreementHolderName" },
        { label: "OS No", key: "osNo" },
        { label: "EP No", key: "epNo" },
      ],
      shouldShowSubSection(parsed.courtDocVerification, "isshowSpecificSuitStatus")
    );

    renderCourt(
      "Appeal",
      parsed.courtDocVerification?.appeal,
      [
        { label: "Lower Court Name", key: "lowerCourtName" },
        { label: "Lower Court Case No", key: "lowerCourtcaseNo" },
        { label: "Appeal Court Name", key: "appealCourtName" },
        { label: "Appeal Case No", key: "appealCaseNo" },
        { label: "Is Lower Court", key: "isLowerCourt" },
        { label: "Lower Court  Judjment reversed", key: "lowerCourtReverse" },
        { label: "Lower Court  Judjement Confirm", key: "alter" },
      ],
      shouldShowSubSection(parsed.courtDocVerification, "isshowAppealStatus")
    );

    renderCourt(
      "Court Auction Purchase",
      parsed.courtDocVerification?.courtAuctionPurchase,
      [
        { label: "OS No", key: "osNo" },
        { label: "EP No", key: "epNo" },
        { label: "Bidder Name", key: "bidderName" },
        { label: "Property Holder Name", key: "propertyHolderName" },
        { label: "Bank Mortgage Involved?", key: "isBankMortageInvolved" },
        { label: "Mother Available?", key: "isMotherAvailable" },
        { label: "Sales Available?", key: "isSalesAvailable" },
      ],
      shouldShowSubSection(parsed.courtDocVerification, "isshowCourtAuctionPurchaseStatus")
    );

    renderCourt(
      "SARFESI Proceeding",
      parsed.courtDocVerification?.sarfesiProceeding,
      [
        {
          label: "Possession  Notice Date(Period 60 day Expired)",
          key: "possessionDate",
        },
        {
          label: "Symbolic Possession Date (Period 60 day Expired)",
          key: "symbolicPossession",
        },
        { label: "Borrower Notice Date", key: "borrowerNotice" },
        { label: "Auction Date", key: "auctionDate" },
        { label: "Paid 15 Percent?", key: "isPaid15Percent" },
        { label: "Sales Certificate Obtained", key: "isSalesCertificate" },
        { label: "Registered", key: "isRegistered" },
      ],
      shouldShowSubSection(parsed.courtDocVerification, "isshowSarfesiProceedingStatus")
    );
  }

  // 14. Original Document Verification
  if (isSectionMeaningful("originalDocVerification")) {
    addSectionTitle(
      "Original Document Verification",
      parsed.originalDocVerification?.bgColor,
    );

    const fields = [
      {
        label: "Title Deed Original availability",
        key: "titleDeedOriginalAvailable",
      },
      {
        label: "(N-1) Immediate parent document original availability",
        key: "immediateParentDocAvailable",
      },
      {
        label: "Last 13 Years document original availability",
        key: "last13YearsDocAvailable",
      },
      {
        label: "Same Extent document original availability",
        key: "sameExtentDocAvailable",
      },
      {
        label: "Larger Extent documents wrongly mentioned original in LOD",
        key: "largerExtentDocWronglyMentioned",
      },
      { label: "Pre Disbursement Verified", key: "preDisbursementVerified" },
      { label: "Post Disbursement Verified", key: "postDisbursementVerified" },
    ];

    const pairs = fields
      .map((f) => ({ label: f.label, value: parsed.originalDocVerification?.[f.key] }))
      .filter(p => isMeaningfulValue(p.value));

    if (pairs.length > 0) {
      addSmartFields(pairs);
    }
  }

  // 15. Remarks
  if (isSectionMeaningful("remarks")) {
    const remarksText = parsed?.remarks || "No remarks added.";
    const splitRemarks = doc.splitTextToSize(remarksText, 180);
    const lineHeight = 6;
    const linesCount = splitRemarks.length;
    let boxHeight = 20 + linesCount * lineHeight; // Base + content
    boxHeight = Math.max(boxHeight, 35); // Minimum height

    checkPageBreak(boxHeight);

    doc.setFillColor(248, 215, 218);
    doc.rect(10, yPos, 190, boxHeight, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(114, 28, 36);
    doc.text("Remarks", 15, yPos + 12);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text(splitRemarks, 15, yPos + 25);

    yPos += boxHeight + 15;
  }

  // Page Numbers
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const pageHeight = doc.internal.pageSize.height;

    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(15, pageHeight - 18, 195, pageHeight - 18);

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 100, 100);
    doc.text(`Page ${i} of ${pageCount}`, 105, pageHeight - 10, {
      align: "center",
    });
  }

  // Save PDF
  const fileName = `${data.file_id}_${data.title.replace(/[^a-zA-Z0-9]/g, "_")}_Checklist_${moment().format("DD-MM-YYYY_HHmm")}.pdf`;
  doc.save(fileName);
};

export default downloadChecklistPDF;